import { useEffect, useRef, useCallback, useState, forwardRef } from "react";
import styles from "./practice.module.scss";

const GAMES = [
  {
    id: "2048",
    title: "2048",
    icon: "/2048.png",
    url: "https://2048.tdbrowse.net/",
    description:
      "Use your arrow keys to move the tiles. When two tiles with the same number touch, they merge into one!",
  },
  {
    id: "4inarow",
    title: "4 in a Row",
    icon: "/4inarow.png",
    url: "https://4-in-a-row.tdbrowse.net/",
    description:
      "Play the classic game of 4 in a Row against another player or an AI opponent.",
  },
];

export default function PracticePage() {
  const [settings, setSettings] = useState({
    dwellTime: 1000,
    activationMethod: "Gaze",
  });
  const buttonsRef = useRef([]);

  // Read eye-tracking settings on mount and listen for changes
  useEffect(() => {
    const readSettings = () => {
      const s = window.eyeTracking?.getSystemSettings();
      if (s) {
        setSettings((prev) => ({
          ...prev,
          dwellTime: s.dwellTime || prev.dwellTime,
          activationMethod: s.activationMethod || prev.activationMethod,
        }));
      }
    };

    readSettings();

    const settingsEventName =
      window.eyeTracking?.getSystemSettings()?.settingsChangedEvent;

    const handleSettingsChanged = () => readSettings();

    if (settingsEventName) {
      document.addEventListener(settingsEventName, handleSettingsChanged);
      return () =>
        document.removeEventListener(settingsEventName, handleSettingsChanged);
    }
  }, []);

  // Set practice-specific favicon
  // This is a bit hacky but allows us to have a custom icon for the practice page without affecting the rest of the site
  useEffect(() => {
    const link = document.querySelector("link[rel='icon']");
    const originalHref = link?.getAttribute("href");
    const originalTitle = document.title;

    if (link) {
      link.setAttribute("href", "/practice.png");
    }

    document.title = "Eye Gaze Practice";

    return () => {
      if (link && originalHref) {
        link.setAttribute("href", originalHref);
      }
      document.title = originalTitle;
    };
  }, []);

  return (
    <div className={styles.page}>
      <h1 className={styles.heading}>Choose a Game to Practice</h1>
      <div className={styles.grid}>
        {GAMES.map((game, idx) => (
          <GameCard
            key={game.id}
            game={game}
            settings={settings}
            ref={(el) => (buttonsRef.current[idx] = el)}
          />
        ))}
      </div>
    </div>
  );
}

const GameCard = forwardRef(function GameCard({ game, settings }, ref) {
  const buttonRef = useRef(null);
  const cardRef = useRef(null);
  const animationRef = useRef(null);
  const activatedRef = useRef(false);

  // Expose button ref to parent if needed
  useEffect(() => {
    if (ref) {
      if (typeof ref === "function") ref(buttonRef.current);
      else ref.current = buttonRef.current;
    }
  }, [ref]);

  // After the card entrance animation ends, force a childList DOM mutation to
  // trigger the MutationObserver so gaze regions are re-detected.
  // CSS animations don't produce DOM mutations, and the region detector rejects
  // elements whose ancestor has opacity: 0 during the animation.  
  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;

    const handleAnimationEnd = (e) => {
      if (e.animationName && e.target === card) {
        // Append and remove a comment node to trigger a childList mutation

        // Note: another approach could be to add a custom attribute and set it at the end on animation.
        // For this to work we would need to add that attribute to the list of known attributes inside 
        // the TD Browse so that mutation observer picks it up. 

        const node = document.createComment("animation-complete");
        card.appendChild(node);
        node.remove();
      }
    };

    card.addEventListener("animationend", handleAnimationEnd);
    return () => card.removeEventListener("animationend", handleAnimationEnd);
  }, []);

  const stopDwellAnimation = useCallback(() => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
    const btn = buttonRef.current;
    if (btn) {
      btn.style.removeProperty("--dwell-progress");
      btn.classList.remove(styles.buttonDwelling);
      btn.classList.remove(styles.buttonActivated);
    }
  }, []);

  const startDwellAnimation = useCallback(
    (dwellTime) => {
      const btn = buttonRef.current;
      if (!btn) return;

      btn.classList.add(styles.buttonDwelling);
      btn.style.setProperty("--dwell-progress", "0%");

      let startTime = null;

      const step = (timestamp) => {
        if (!startTime) startTime = timestamp;
        const elapsed = timestamp - startTime;
        const progress = Math.min((elapsed / dwellTime) * 100, 100);
        btn.style.setProperty("--dwell-progress", `${progress}%`);

        if (progress < 100) {
          animationRef.current = requestAnimationFrame(step);
        } else {
          animationRef.current = null;
        }
      };

      animationRef.current = requestAnimationFrame(step);
    },
    []
  );

  const handleGazeOver = useCallback(
    (e) => {
      if (e.target.hasAttribute("disabled")) return;
      activatedRef.current = false;
      startDwellAnimation(settings.dwellTime);
    },
    [settings.dwellTime, startDwellAnimation]
  );

  const handleGazeOut = useCallback(() => {
    activatedRef.current = false;
    stopDwellAnimation();
  }, [stopDwellAnimation]);

  const handleGazeActivate = useCallback(
    (e) => {
      if (e.target.hasAttribute("disabled")) return;

      activatedRef.current = true;

      // Mark as activated (keep full border visible)
      const btn = buttonRef.current;
      if (btn) {
        btn.classList.add(styles.buttonActivated);
      }

      // If activation method is 'Gaze', clear animation immediately
      if (settings.activationMethod !== "GazeAndSwitch") {
        stopDwellAnimation();
      }
      // If 'GazeAndSwitch', animation stays until gazeout

      // Navigate to game
      window.location.href = game.url;
    },
    [settings.activationMethod, stopDwellAnimation, game.url]
  );

  useEffect(() => {
    const btn = buttonRef.current;
    if (!btn) return;

    btn.addEventListener("gazeover", handleGazeOver);
    btn.addEventListener("gazeout", handleGazeOut);
    btn.addEventListener("gazeactivate", handleGazeActivate);

    return () => {
      btn.removeEventListener("gazeover", handleGazeOver);
      btn.removeEventListener("gazeout", handleGazeOut);
      btn.removeEventListener("gazeactivate", handleGazeActivate);
    };
  }, [handleGazeOver, handleGazeOut, handleGazeActivate]);

  return (
    <div ref={cardRef} className={styles.card}>
      <img className={styles.icon} src={game.icon} alt={game.title} />
      <p className={styles.description}>{game.description}</p>
      <a
        ref={buttonRef}
        href={game.url}
        className={styles.button}
        data-gaze-region
        data-gaze-region-margin="2vw"
      >
        Play {game.title}
      </a>
    </div>
  );
});
